import { ColorEnum } from '@/constants/color'
import { Layout } from '@/features/layout/types'
import Header from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/Header'
import PaymentTotals from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/PaymentTotals'
import ReconciliationStatus from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/ReconciliationStatus'
import ShiftCloseoutSummary from '@/features/shiftCloseouts/components/ShiftCloseoutForm/parts/ShiftCloseoutSummary'
import { useFormSubmit } from '@/features/shiftCloseouts/form'
import { FormOutput } from '@/features/shiftCloseouts/form/types'
import { useShiftCloseoutReconciliation } from '@/features/shiftCloseouts/hooks/useShiftCloseoutReconciliation'
import { DailyShiftCloseout } from '@/features/shiftCloseouts/types'
import { formatShiftCloseoutDate } from '@/features/shiftCloseouts/utils/formatShiftCloseoutDate'
import Button from '@/features/ui/buttons/components/Button'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface ShiftCloseoutFormProps {
    layout: Layout
    onBack: () => void
    onSubmit: (form: FormOutput) => void
    profileName: string
    shiftCloseout: DailyShiftCloseout
}

const ShiftCloseoutForm: FC<ShiftCloseoutFormProps> = ({
    layout,
    onBack,
    onSubmit,
    profileName,
    shiftCloseout,
}) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { layout })
    const reconciliation = useShiftCloseoutReconciliation(shiftCloseout)
    const [{ isSubmitting, isValid }, submit] = useFormSubmit(onSubmit)

    return (
        <ScrollView
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="always"
            showsVerticalScrollIndicator={false}
        >
            <Header
                closedAt={formatShiftCloseoutDate(shiftCloseout.closedAt)}
                layout={layout}
                onBack={onBack}
                profileName={profileName}
            />

            <View style={styles.content}>
                <View style={styles.summaryPanel}>
                    <ShiftCloseoutSummary
                        paymentMethods={shiftCloseout.paymentMethods}
                        preview={shiftCloseout.preview}
                        reconciliation={reconciliation}
                    />
                </View>

                <View style={styles.paymentPanel}>
                    <PaymentTotals
                        layout={layout}
                        paymentMethods={shiftCloseout.paymentMethods}
                        reconciliation={reconciliation}
                    />

                    <View style={styles.bottomContent}>
                        <ReconciliationStatus reconciliation={reconciliation} />
                        <Button
                            color="action"
                            disabled={!isValid || !reconciliation.isComplete || isSubmitting}
                            onPress={submit}
                            style={styles.submit}
                            testID="review-shift-closeout-button"
                        >
                            {t('shiftCloseouts.create.submit').toUpperCase()}
                        </Button>
                    </View>
                </View>
            </View>
        </ScrollView>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    scrollContent: {
        flexGrow: 1,
        paddingBottom: insets.bottom,
    },
    content: {
        flexGrow: 1,
        variants: {
            layout: {
                compact: {
                    flexDirection: 'column',
                },
                extended: {
                    flexDirection: 'row',
                },
            },
        },
    },
    summaryPanel: {
        padding: 16,
        variants: {
            layout: {
                compact: {
                    width: '100%',
                    paddingLeft: 16 + insets.left,
                    paddingRight: 16 + insets.right,
                },
                extended: {
                    width: 440,
                },
            },
        },
    },
    paymentPanel: {
        flex: 1,
        minWidth: 0,
        gap: 24,
        justifyContent: 'space-between',
        padding: 16,
        variants: {
            layout: {
                compact: {
                    borderTopColor: ColorEnum.GRAY_200,
                    borderTopWidth: 1,
                    paddingLeft: 16 + insets.left,
                    paddingRight: 16 + insets.right,
                },
                extended: {
                    borderLeftColor: ColorEnum.GRAY_200,
                    borderLeftWidth: 1,
                },
            },
        },
    },
    bottomContent: {
        gap: 16,
    },
    submit: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
}))

export default ShiftCloseoutForm
